import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuthContext } from '@/modules/auth/context/AuthProvider';
import { authFetch } from '@/modules/auth/utils/authFetch';
import * as Sentry from '@sentry/browser';

const SubscriptionContext = createContext(null);

// Cross-instance in-flight dedup: concurrent providers for the same user
// (StrictMode remounts, multiple hook consumers mounting together) share
// one network request. Cleared on settle, so data is never served stale.
const inflightSubscriptionRequests = new Map();

function fetchSubscriptionStatus(userId, accessToken) {
  let inflight = inflightSubscriptionRequests.get(userId);
  if (!inflight) {
    inflight = (async () => {
      const response = await authFetch('/api/subscription-status', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        const error = new Error(`HTTP ${response.status}: ${errorData.error || 'Unknown error'}`);
        error.status = response.status;
        throw error;
      }

      return response.json();
    })();
    inflightSubscriptionRequests.set(userId, inflight);
    // .then(success, failure) — not .finally — so the derived promise
    // resolves and never triggers unhandled-rejection warnings
    inflight.then(
      () => { if (inflightSubscriptionRequests.get(userId) === inflight) inflightSubscriptionRequests.delete(userId); },
      () => { if (inflightSubscriptionRequests.get(userId) === inflight) inflightSubscriptionRequests.delete(userId); }
    );
  }
  return inflight;
}

export function SubscriptionProvider({ children }) {
  const [subscription, setSubscription] = useState({
    hasActiveSubscription: false,
    subscriptionStatus: null,
    trialEnd: null,
    customerId: null,
    loading: true,
    error: null
  });
  
  const { user, session } = useAuthContext();
  const requestIdRef = useRef(0);
  const lastUserIdRef = useRef(null);
  // Bumped by refetch() to re-run the status check below (e.g. right after
  // a trial starts or checkout completes — the effect otherwise considers
  // data for the current user fresh and would serve stale gating state).
  const [refreshNonce, setRefreshNonce] = useState(0);

  const refetch = useCallback(() => {
    lastUserIdRef.current = null;
    setRefreshNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    let isMounted = true;
    // Generation counter: only the latest run may write state, so a stale
    // in-flight request can never swallow the result and leave loading stuck.
    const requestId = ++requestIdRef.current;
    const isLatest = () => isMounted && requestId === requestIdRef.current;

    const initializeSubscription = async () => {
      // Skip if we already have fresh data for this user
      if (lastUserIdRef.current === user.id) {
        console.log('Skipping subscription initialization - data already fresh for user');
        return;
      }

      setSubscription(prev => ({ ...prev, loading: true, error: null }));

      try {
        console.log('Initializing subscription for user:', user.id);

        // Lookup-only subscription status (Stripe customers created at checkout).
        // Concurrent instances share the in-flight request.
        const data = await fetchSubscriptionStatus(user.id, session.access_token);
        console.log('Subscription data retrieved:', data);

        if (isLatest()) {
          setSubscription(prev => ({
            ...prev,
            loading: false,
            hasActiveSubscription: data.hasActiveSubscription || false,
            subscriptionStatus: data.subscriptionStatus,
            trialEnd: data.trialEnd ? new Date(data.trialEnd) : null,
            customerId: data.customerId,
            error: null
          }));
        }
      } catch (error) {
        console.error('Error initializing subscription:', error);

        Sentry.captureException(error, {
          extra: {
            userId: user?.id,
            hasSession: !!session,
            hasAccessToken: !!session?.access_token,
            errorMessage: error.message
          }
        });

        // Handle authentication errors gracefully
        if (error.status === 401) {
          console.log('Authentication failed for subscription check');
          if (isLatest()) {
            setSubscription(prev => ({
              ...prev,
              loading: false,
              hasActiveSubscription: false,
              subscriptionStatus: null,
              trialEnd: null,
              customerId: null,
              error: 'Authentication required'
            }));
          }
          return;
        }

        if (isLatest()) {
          setSubscription(prev => ({
            ...prev,
            loading: false,
            error: error.message
          }));
        }
      } finally {
        // Only the latest run marks this user as fresh; a stale run must not
        // claim freshness or a later run would early-return on stale data.
        if (requestId === requestIdRef.current) {
          lastUserIdRef.current = user.id;
        }
      }
    };

    if (user && session?.access_token) {
      initializeSubscription();
    } else {
      // Reset subscription state when user logs out
      if (isMounted) {
        setSubscription({
          hasActiveSubscription: false,
          subscriptionStatus: null,
          trialEnd: null,
          customerId: null,
          loading: false,
          error: null
        });
      }
      lastUserIdRef.current = null;
    }

    return () => {
      isMounted = false;
    };
  }, [user?.id, session?.access_token, refreshNonce]);


  // Calculate derived values
  const isPremium = subscription.hasActiveSubscription && 
    (subscription.subscriptionStatus === 'active' || subscription.subscriptionStatus === 'trialing');
  
  const isTrialing = subscription.subscriptionStatus === 'trialing';
  
  const trialDaysRemaining = subscription.trialEnd ? 
    Math.max(0, Math.ceil((subscription.trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : 0;

  const contextValue = {
    ...subscription,
    isPremium,
    isTrialing,
    trialDaysRemaining,
    refetch
  };

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscriptionContext() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
  }
  return context;
}